'use client';

import { Box, Divider, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useDockviewApi } from '@/app/providers/DockviewApiProvider';
import { useGobackTab } from '@/features/tab/tab-history';
import { useForwardTab } from '@/features/tab/tab-history';
import { ToolbarIconButton } from '@/widgets/tab/tab-toolbar/ui/ToolbarIconButton';
import { ToolbarSlot } from '@/widgets/tab/tab-toolbar/ui/ToolbarSlot';
import { TabToolbarConfig } from '@/entities/tab/model/tab.types';

const MOBILE_BREAKPOINT = 600;

interface TabToolbarProps {
  tabId: string;
  config?: TabToolbarConfig | null;
}

export function TabToolbar({ tabId, config = null }: TabToolbarProps) {
  const api = useDockviewApi();
  const { goback, canGoBack } = useGobackTab(tabId, api);
  const { forward, canGoForward } = useForwardTab(tabId, api);
  /** noSsr: 뷰포트 기준 모바일 여부만 사용 — SSR/하이드레이션과 불일치로 툴바가 한 프레임 달라지는 것 완화 */
  const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT}px)`, { noSsr: true });

  const hasCustomSlots =
    config &&
    ((config.left && config.left.length > 0) ||
      config.center ||
      (config.right && config.right.length > 0));

  // 모바일에서 이전/이후 숨기고 커스텀 슬롯도 없으면 툴바 전체 숨김
  if (isMobile && !hasCustomSlots) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        padding: '4px 8px',
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        height: '40px',
        minHeight: '40px',
        maxHeight: '40px',
      }}
    >
      {!isMobile && (
        <>
          <ToolbarIconButton
            icon={ArrowBackIcon}
            tooltip="뒤로 가기"
            size="small"
            onClick={goback}
            disabled={!canGoBack}
          />
          <ToolbarIconButton
            icon={ArrowForwardIcon}
            tooltip="앞으로 가기"
            size="small"
            onClick={forward}
            disabled={!canGoForward}
          />
        </>
      )}

      {hasCustomSlots && (
        <>
          {!isMobile && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}

          {config.left && config.left.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {config.left.map((slot, index) => (
                <ToolbarSlot key={`left-${index}`} slot={slot} index={index} />
              ))}
            </Box>
          )}

          {config.center && (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {config.center}
            </Box>
          )}

          {config.right && config.right.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginLeft: 'auto' }}>
              {config.right.map((slot, index) => (
                <ToolbarSlot key={`right-${index}`} slot={slot} index={index} />
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
